import { useEffect, useMemo } from 'react';
import { Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import FullScreenLoader from 'components/FullScreenLoader';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { useAuth } from 'contexts/AuthContext';
import privatePage from 'hocs/privatePage';
import useGraph from 'hooks/api/graphs/useGraph';
import useStore from 'state/appState';
import { makeGraphURL } from 'utils';

import ErrorScreen from './ErrorScreen';

const AppView = dynamic(() => import('views/App'), { ssr: false });

export const views = [
  // 'graph',
  // 'dashboard',
  // 'chat',
  'data',
  'requests',
  'queries',
  // 'customize',
  'knowledge',
  'documents',
  // 'settings',
  'home',
  'members',
  'storages',
];

function App() {
  const router = useRouter();
  const queryClient = useQueryClient();

  const {
    data: graph,
    isLoading,
    error,
  } = useGraph({
    select: (g) => ({
      uid: g.uid,
      slug: g.slug,
      storage_uid: g.storage_uid,
      organization_uid: g.organization_uid,
    }),
  });
  const { isAuthenticated } = useAuth();

  const isChangingGraph = useMemo(
    () => !(router.query.id as string) || !graph?.uid || (router.query.id as string) !== graph?.uid,
    [router.query.id, graph?.uid],
  );

  const resetGraph = useStore((s) => s.resetGraph);

  useEffect(() => {
    return () => {
      if (!graph?.uid) return;
      // reset state before we remove the queries
      resetGraph();
      queryClient.removeQueries(['graph']);
    };
  }, [resetGraph, router.query.id, graph?.uid, queryClient]);

  // when app loads, if there's no view value in the url -> give it a default value ('home')
  useEffect(() => {
    const pathParts = window.location.pathname.split('/');
    const uid = pathParts.length > 2 ? pathParts[2] : undefined;
    const slug = pathParts.length > 3 ? pathParts[3] : undefined;
    const view = pathParts.length > 4 ? pathParts[4] : undefined;
    if (!uid) return;
    if (!view || !views.includes(view)) {
      const url = makeGraphURL({ uid: uid, view: 'home', slug: slug });
      router.replace(url);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isLoading || isChangingGraph) return;
    if ((router.query.id as string) !== graph?.uid) {
      return; // skip if graph uid doesn't match router uid
    }
    // if graph.slug doesn't equal query slug -> replace the url
    if (graph?.uid && graph?.slug && graph.slug.toLocaleLowerCase() !== router.query.slug) {
      const view = router.query.view ? (router.query.view as string) : undefined;
      router.replace({
        pathname: makeGraphURL({ uid: graph?.uid, slug: graph.slug, view: view }),
        query: { ...Object.fromEntries(new URLSearchParams(window.location.search)) },
      });
    }
  }, [
    graph?.slug,
    graph?.uid,
    router,
    router.query.slug,
    router.query.id,
    router.query.view,
    isLoading,
    isChangingGraph,
  ]);

  return (
    <Flex minH="100%" h="100%" direction="column">
      <Flex grow={1}>
        {error ? (
          <ErrorScreen error={error} isAuthenticated={isAuthenticated} />
        ) : (
          <>{isLoading || isChangingGraph ? <FullScreenLoader /> : <AppView />}</>
        )}
      </Flex>
    </Flex>
  );
}

export default privatePage(App);
